// Race.js
// Centralized object for all race-related state and file info
// files like main.js, common.js, polar-builder.js and drive-helper.js should use this file
// rather than passing individual fields like file ids, file names or file data

import * as bd from './boat-data.js';
import moment from 'moment';
import { Maneuvers } from './Maneuvers.js';
import { RaceTracks } from './RaceTracks.js';
import { Info } from './Info.js';
import chroma from 'chroma-js';
import { perfLog } from '../utils/perf-utils.js';

export const BOAT_COLORS_ARR = ["#000000", ...chroma.brewer.Set1, "#92c8ffff", "#942eb6ff", "#2eb672ff", "#e6a582ff"];

const COURSE_POINT_INTERVAL_MS = 30 * 60 * 1000;

// Downsamples the vessel track to ~30-min-spaced {lat,lon} points, used by ZephyrProvider
// to score weather stations by proximity to the race course (spec 24, D4).
function sampleCoursePoints(frames, intervalMs = COURSE_POINT_INTERVAL_MS) {
  const points = [];
  let lastT = -Infinity;
  frames.forEach(f => {
    const t = f.t.valueOf();
    if (t - lastT >= intervalMs) {
      points.push({ lat: f.lat, lon: f.lon });
      lastT = t;
    }
  });
  return points;
}

export class Race {

  constructor(sail, info) {
    this._sail = sail;
    this._info = info;
    this._raceTracks = null;
    this.startPointCache = null;
    this._extent = null;
  }

  getRaceTracks() {
    return this._raceTracks;
  }

  buildRaceTracks() {
    try {
      const course = this._info.getCourse();
      if (!course) return null;
      this._raceTracks = new RaceTracks(this._sail, this._info);
      this._raceTracks.calcRacePositions();
      return this._raceTracks;
    } catch (e) {
      console.log("error building race tracks:", e);
    }
    return null;
  }

  setFileNames(fileName) {
    this._sail.raceFileName = fileName;
    this._info.fileName = Info.infoFileNameFor(fileName);
  }

  info() {
    return this._info;
  }

  sail() {
    return this._sail;
  }

  extent() {
    if (this._extent) return this._extent;
    let minLon = Infinity, maxLon = -Infinity;
    let minLat = Infinity, maxLat = -Infinity;
    let startMs = Infinity, endMs = -Infinity;
    this._sail.getTracks().forEach(track => {
      track.getFrames().forEach(f => {
        minLon = Math.min(minLon, f.lon);
        maxLon = Math.max(maxLon, f.lon);
        minLat = Math.min(minLat, f.lat);
        maxLat = Math.max(maxLat, f.lat);
        const t = f.t.valueOf();
        if (t < startMs) startMs = t;
        if (t > endMs) endMs = t;
      });
    });
    const DEG_TO_KM = 111.195;
    const midLat = (minLat + maxLat) / 2;
    const widthKm = (maxLon - minLon) * DEG_TO_KM * Math.cos(midLat * Math.PI / 180);
    const heightKm = (maxLat - minLat) * DEG_TO_KM;
    const vesselTrack = this._sail.getTracks()[0];
    const coursePoints = sampleCoursePoints(vesselTrack ? vesselTrack.getFrames() : []);
    this._extent = { minLat, maxLat, minLon, maxLon, widthKm, heightKm, startMs, endMs, coursePoints };
    return this._extent;
  }

  getStartPoint() {
    if (this.startPointCache != null)
      return this.startPointCache;

    let startPoint = this._sail.getStartPoint();
    if (startPoint == null) {
      startPoint = this._info.getCourseStartTime();
      if (startPoint != null) {
        startPoint = startPoint.clone().subtract(5, 'minutes');
      }
    }
    this.startPointCache = startPoint;
    return startPoint;
  }

  static async loadFromNMEA(text, fileName, notifyFn = null, settings = null) {
    const sailingData = await bd.parseNmeaData(text, notifyFn);
    // bd.interpolateAIS(sailingData);
    const race = new Sail(sailingData, fileName, null);
    race.calculateFields(settings);
    const raceInfo = new Info(null, Info.infoFileNameFor(fileName), null);
    return new Race(race, raceInfo);
  }

}

const WIND_SHORT_WINDOW_SEC = 5;
const WIND_LONG_WINDOW_SEC = 5 * 60;
const TWA_CENTRED_AVERAGE_WINDOW_SEC = 10;

/**
 * this.data contains:
 {
  vessel: {
    shipName: "Sliver",
    data: [
      {t:obj:moment, twa: 89, tws: 12.1, bs: 6.5, lat: 41.19, lon: 174.50, sog: 5.87, th: 321.3},
    ]
  },
  ais:[
      shipName: "Sliver",
      mmsi: 123456789,
      data: [
        {t:obj:moment, lat: 41.19, lon: 174.50, sog: 5.87, th: 321.3},
      ]
    ],
    923456789: { ... }
  }
}

 Example frame
 { t:"2025-07-06T01:44:34.000Z",
  twa:220,
  tws:20,
  bs:8.89,
  lat:-41.24225467948718,lon:174.86706589743602,
  sog:8.93,
  th:331,
  awa:243,
  aws:14.71,
  p:-1.22,
  r:6.82,
  _cog:341,
  _twd:201,
  _awd:224,
  _vmg:6.81,
  calc: {
    leewayDeg: 9.36,
    twa_avg_centered: 123
    twa_avg_short: 123
    }
  } 
 */
export class Sail {

  constructor(sailingData, fileName, docId) {
    this.fileId = docId || null;
    this.fileName = fileName || null;
    this.data = sailingData || null;
    this.raceFileCompress = false;
    this.maneuvers = null;

    this.useCalculatedTwa = false;
    this.useSOG = false;
    this.useLeeway = true;
    this.leewayK = 10;
    this.tracks = this.createTracks();
  }

  createTracks() {
    const tracks = [];
    tracks.push(new SailTrack(this.data.vessel, 0, SailTrack.TYPE.VESSEL));
    if (this.data.ais) {
      for (let i = 0; i < this.data.ais.length; i++) {
        tracks.push(new SailTrack(this.data.ais[i], i + 1, SailTrack.TYPE.AIS));
      }
    }
    return tracks;
  }

  updatePlayIndices(playTsSeconds) {
    this.tracks.forEach(track => {
      let idx = track.getFrameIndexAtTs(playTsSeconds);
      if (idx == -1)
        idx = track.getFrames().length - 1;
      track.setPlayIndex(idx);
      track.setPlayTs(playTsSeconds);
    });
  }

  keepTheseAisTracks(mmsiArray) {
    const mmsiArrayStr = mmsiArray.map(x => String(x));
    const newAis = this.data.ais.filter((x) => mmsiArrayStr.includes(String(x.mmsi)));
    this.data.ais = newAis;
    this.tracks = this.createTracks();
  }

  // needs to be called after constructor
  calculateFields(settings = null) {
    console.log("Calculating fields");
    const calcWind = settings?.getCalculatedWind?.() || {};
    this.useCalculatedTwa = calcWind.enabled || false;
    this.useSOG = calcWind.useSOG || false;
    this.useLeeway = calcWind.useLeeway !== false;
    this.leewayK = calcWind.leewayK || 10;

    if (this.data?.vessel?.data) {
      this.calculateLeeway();
      this.calculateTWAWithLeeway();
      this.calculateTWACentredMovingAvg();
      this.calculateWindMovingAvgShort();
      this.calculateWindMovingAvgLong()
      this.maneuvers = new Maneuvers(this.data?.vessel?.data);
    }

    if (settings?.isPolarCached?.()) {
      this.calculatePolarPerformance(settings.getDefaultPolarCache());
    }
    //console.log("Calculated Fields:",this);
  }

  getTracks() {
    return this.tracks;
  }

  pruneAIS() {
    bd.pruneInterpolatedAIS(this.data);
  }

  interpolateAIS() {
    bd.interpolateAIS(this.data);
  }

  getVesselTrack() {
    return this.tracks.length > 0 ? this.tracks[0] : null;
  }

  getFirstTsSeconds() {
    let firstTs = Number.MAX_SAFE_INTEGER;
    for (const track of this.tracks) {
      const frames = track.getFrames();
      if (frames && frames.length > 0) {
        firstTs = Math.min(firstTs, frames[0].t.unix());
      }
    }
    return firstTs === Number.MAX_SAFE_INTEGER ? 0 : firstTs;
  }

  getLastTsSeconds() {
    let lastTs = 0;
    for (const track of this.tracks) {
      const frames = track.getFrames();
      if (frames && frames.length > 0) {
        lastTs = Math.max(lastTs, frames[frames.length - 1].t.unix());
      }
    }
    return lastTs;
  }

  frameCog(f) {
    if (!f) return -1;
    return (f._cog || f.cog || -1);
  }

  frameTwa(f) {
    if (!f) return -1;
    const r = this.useCalculatedTwa ? f.calc?.twa : f.twa;
    return r != null ? r : -1;
  }

  frameTws(f) {
    if (!f) return -1;
    const r = this.useCalculatedTwa ? f.calc?.tws : f.tws;
    return r != null ? r : -1;
  }

  frameTwd(f) {
    if (!f) return -1;
    const r = this.useCalculatedTwa ? f.calc?.twd : f._twd;
    return r != null ? r : -1;
  }

  frameAwd(f) {
    if (!f) return -1;
    const r = this.useCalculatedTwa ? f.calc?.awd : f._awd;
    return r != null ? r : -1;
  }

  frameAws(f) {
    if (!f) return -1;
    const r = this.useCalculatedTwa ? f.calc?.aws : f.aws;
    return r != null ? r : -1;
  }

  frameAwa(f) {
    if (!f) return -1;
    const r = this.useCalculatedTwa ? f.calc?.awa : f.awa;
    return r != null ? r : -1;
  }

  frameTwaAvgShort(f) {
    if (!f) return null;
    return f.calc?.twa_avg_short;
  }

  frameTwdAvgShort(f) {
    if (!f) return null;
    return f.calc?.twd_avg_short;
  }

  frameTwsAvgShort(f) {
    if (!f) return null;
    return f.calc?.tws_avg_short;
  }

  frameTwdAvgLong(f) {
    if (!f) return null;
    return f.calc?.twd_avg_long;
  }

  frameTwsAvgLong(f) {
    if (!f) return null;
    return f.calc?.tws_avg_long;
  }

  frameAwaAvgShort(f) {
    if (!f) return null;
    return f.calc?.awa_avg_short;
  }

  frameAwdAvgShort(f) {
    if (!f) return null;
    return f.calc?.awd_avg_short;
  }

  frameAwsAvgShort(f) {
    if (!f) return null;
    return f.calc?.aws_avg_short;
  }

  frameAddCalcField(f, name, value) {
    if (f?.calc == null) f.calc = {};
    f.calc[name] = value;
  }

  static frameGetCalcField(f, name) {
    if (f?.calc == null) return null;
    return f.calc[name];
  }

  setFrameLegId(frame, legId) {
    this.frameAddCalcField(frame, 'legId', legId);
  }

  frameLegId(frame) {
    return Sail.frameGetCalcField(frame, 'legId');
  }

  setFrameProgressM(frame, progressM) {
    this.frameAddCalcField(frame, 'progressM', progressM);
  }

  frameProgressM(frame) {
    return Sail.frameGetCalcField(frame, 'progressM');
  }

  setFrameTimeDelta(frame, timeDelta) {
    const p = this.frameAddCalcField(frame, 'timeDeltaSec', timeDelta);
    return p == null ? 0 : p;
  }

  frameTimeDelta(frame) {
    const t = Sail.frameGetCalcField(frame, 'timeDeltaSec');
    return t == null ? 0 : t;
  }

  hasFileId() {
    return this.fileId != null;
  }

  setFileId(id) {
    this.fileId = id;
  }

  setSailingData(d) {
    this.data = d;
    this.calculateFields();
  }

  getManeuversList() {
    return this.maneuvers?.getManeuvers();
  }


  calculateLeeway() {
    const frames = this.data.vessel.data;
    for (let i = 0; i < frames.length; i++) {
      const frame = frames[i];
      if (!frame.calc) frame.calc = {};

      if (frame.r != null && frame.r != NaN && frame.bs != null && frame.bs > 0.1) {
        frame.calc.leewayDeg = parseFloat((this.leewayK * frame.r / (frame.bs * frame.bs)).toFixed(2));
      } else {
        frame.calc.leewayDeg = null;
      }
    }
  }

  getLeewayAbs(idx) {
    const leeway = this.data?.vessel?.data[idx]?.calc?.leewayDeg;
    return leeway != null ? Math.abs(leeway) : null;
  }

  calculateTWAWithLeeway() {
    const frames = this.data.vessel.data;
    for (let i = 0; i < frames.length; i++) {
      const frame = frames[i];
      if (!frame.calc) frame.calc = {};

      const speed = this.useSOG ? frame.sog : frame.bs;
      let leeway = (this.useLeeway && frame.calc.leewayDeg != null) ? frame.calc.leewayDeg : 0;
      if (isNaN(leeway)) leeway = 0;

      if (frame.awa != null && frame.aws != null && speed != null) {
        const adjustedAWA = frame.awa + leeway;
        const tw = bd.calculateTrueWind(speed, adjustedAWA, frame.aws);
        frame.calc.awa = adjustedAWA;
        frame.calc.aws = frame.aws;
        frame.calc.twa = tw.twa;
        frame.calc.tws = tw.tws;
        frame.calc.twd = frame._cog != null ? (frame._cog + frame.calc.twa) % 360 : null;
        frame.calc.awd = frame._cog != null ? (frame._cog + adjustedAWA) % 360 : null;
      } else {
        frame.calc.twa = null;
        frame.calc.tws = null;
        frame.calc.twd = null;
        frame.calc.awd = null;
      }
    }
  }

  calculateTWACentredMovingAvg() {
    const frames = this.data.vessel.data;
    const halfWindow = TWA_CENTRED_AVERAGE_WINDOW_SEC / 2;

    for (let i = 0; i < frames.length; i++) {
      const windowStartTime = frames[i].t.clone().subtract(halfWindow, 'seconds');
      const windowEndTime = frames[i].t.clone().add(halfWindow, 'seconds');

      let windowStartIdx = i;
      while (windowStartIdx > 0 && frames[windowStartIdx].t.isAfter(windowStartTime)) {
        windowStartIdx--;
      }

      let windowEndIdx = i;
      while (windowEndIdx < frames.length - 1 && frames[windowEndIdx].t.isBefore(windowEndTime)) {
        windowEndIdx++;
      }

      const twas = frames.slice(windowStartIdx, windowEndIdx + 1)
        .map(f => this.frameTwa(f))
        .filter(twa => twa != null && !isNaN(twa));

      if (!frames[i].calc) frames[i].calc = {};
      frames[i].calc.twa_avg_centered = twas.length > 0 ? bd.averageAngle(twas) : null;
    }
  }

  calculateWindMovingAvgShort() {
    const frames = this.data.vessel.data;
    for (let i = 0; i < frames.length; i++) {
      const windowStartTime = frames[i].t.clone().subtract(WIND_SHORT_WINDOW_SEC, 'seconds');

      let windowStartIdx = i;
      while (windowStartIdx > 0 && frames[windowStartIdx].t.isAfter(windowStartTime)) {
        windowStartIdx--;
      }

      if (!frames[i].calc) frames[i].calc = {};

      const twas = frames.slice(windowStartIdx, i + 1)
        .map(f => this.frameTwa(f))
        .filter(twa => twa != null && !isNaN(twa));
      frames[i].calc.twa_avg_short = twas.length > 0 ? bd.averageAngle(twas) : null;

      const twds = frames.slice(windowStartIdx, i + 1)
        .map(f => this.frameTwd(f))
        .filter(twd => twd != null && !isNaN(twd));
      frames[i].calc.twd_avg_short = twds.length > 0 ? bd.averageAngle(twds) : null;

      const twss = frames.slice(windowStartIdx, i + 1)
        .map(f => this.frameTws(f))
        .filter(tws => tws != null && !isNaN(tws));
      frames[i].calc.tws_avg_short = twss.length > 0 ? bd.averageValue(twss, 1) : null;

      const awas = frames.slice(windowStartIdx, i + 1)
        .map(f => this.frameAwa(f))
        .filter(awa => awa != null && !isNaN(awa));
      frames[i].calc.awa_avg_short = awas.length > 0 ? bd.averageAngle(awas) : null;

      const awds = frames.slice(windowStartIdx, i + 1)
        .map(f => this.frameAwd(f))
        .filter(awd => awd != null && !isNaN(awd));
      frames[i].calc.awd_avg_short = awds.length > 0 ? bd.averageAngle(awds) : null;

      const awss = frames.slice(windowStartIdx, i + 1)
        .map(f => this.frameAws(f))
        .filter(aws => aws != null && !isNaN(aws));
      frames[i].calc.aws_avg_short = awss.length > 0 ? bd.averageValue(awss, 1) : null;
    }
  }

  calculateWindMovingAvgLong() {
    const frames = this.data.vessel.data;
    for (let i = 0; i < frames.length; i++) {
      //const windowStartTime = frames[i].t.clone().subtract(WIND_LONG_WINDOW_SEC, 'seconds');
      const windowStartMs = frames[i].t.valueOf() - WIND_LONG_WINDOW_SEC * 1000;
      let windowStartIdx = i;
      //while (windowStartIdx > 0 && frames[windowStartIdx].t.isAfter(windowStartTime)) {
      while (windowStartIdx > 0 && frames[windowStartIdx].t.valueOf() > windowStartMs) {
        windowStartIdx--;
      }

      if (!frames[i].calc) frames[i].calc = {};

      const twds = frames.slice(windowStartIdx, i + 1)
        .map(f => this.frameTwd(f))
        .filter(twd => twd != null && !isNaN(twd));
      frames[i].calc.twd_avg_long = twds.length > 0 ? bd.averageAngle(twds) : null;

      const twss = frames.slice(windowStartIdx, i + 1)
        .map(f => this.frameTws(f))
        .filter(tws => tws != null && !isNaN(tws));
      frames[i].calc.tws_avg_long = twss.length > 0 ? bd.averageValue(twss, 1) : null;
    }
  }

  calculatePolarPerformance(polar) {
    if (!this.data?.vessel?.data || !polar) return;

    const frames = this.data.vessel.data;
    for (let i = 0; i < frames.length; i++) {
      const frame = frames[i];

      if (frame.calc.twa_avg_short == null || frame.calc.tws_avg_short == null || frame.bs == null) {
        frame.calc.polarPerf = null;
        continue;
      }

      const polarTarget = polar.interpolateSpeed(frame.calc.twa_avg_short, frame.calc.tws_avg_short);
      if (polarTarget == null) {
        frame.calc.polarPerf = null;
        continue;
      }

      frame.calc.polarPerf = Math.floor((frame.bs / polarTarget) * 100);
    }
  }

  static removeCalculatedFields(frames) {
    for (let i = 0; i < frames.length; i++) {
      delete frames[i].calc;
    }
  }


  static async loadFromJSON(text, fileName, docId, settings = null, notifyFn = null) {
    let sailingData = null;
    if (text != null) {
      if (notifyFn != null) await notifyFn("Leaving the Dock");
      sailingData = bd.importJSON(text, notifyFn);
      perfLog('Sail.loadFromJSON: bd.importJSON done');
      if (notifyFn != null) await notifyFn("Hoisting sails");
      bd.interpolateAIS(sailingData);
      perfLog('Sail.loadFromJSON: bd.interpolateAIS done');
    }
    const sail = new Sail(sailingData, fileName, docId);
    if (notifyFn != null) await notifyFn("Finding a lane");
    sail.calculateFields(settings);
    perfLog('Sail.loadFromJSON: calculateFields done');
    return sail;
  }

  serialiseToJSON() {
    if (!this.data) return null;

    const data = JSON.parse(JSON.stringify(this.data));
    bd.pruneInterpolatedAIS(data);
    if (data.vessel?.data) {
      Sail.removeCalculatedFields(data.vessel.data);
    }
    return JSON.stringify(data, null, 2);
  }

  // setStartPoint(ts) {
  //   if (this.data != null) {
  //     this.data.startPoint = ts;
  //     this.startPointCache = ts;
  //   }
  // }


  getStartPoint() {
    if (this.startPointCache != null)
      return this.startPointCache;
    if (this.data != null) {
      if (typeof this.data.startPoint !== "undefined" && this.data.startPoint != null) {
        const ts = moment(this.data.startPoint);
        this.startPointCache = ts;
        return ts;
      } else {
        return null;
      }
    }
  }


}

/** 
 * This object wraps ship, we use it to access data in ship which is persisted
 * the object also has many other fields which are not persisted
*/
export class SailTrack {

  static TYPE = {
    VESSEL: "vessel",
    AIS: "ais",
  };

  constructor(ship, id, type) {
    this.ship = ship;
    this.id = id;
    this.type = type;
    this.color = null;
    this.handicap = null;
    this.ui_currentFrameIndex = null;
    this.ui_playTs = null;
    this.raceProgress = null;
  }

  getId() {
    return this.id;
  }
  getMMSI() {
    return this.type == SailTrack.TYPE.VESSEL ? null : this.ship.mmsi;
  }
  getType() {
    return this.type;
  }

  isTypeVessel() {
    return this.type === SailTrack.TYPE.VESSEL;
  }

  isTypeAIS() {
    return this.type === SailTrack.TYPE.AIS;
  }

  getShipName(settings = null) {
    if (settings) {
      const raceVessel = this.isTypeVessel()
        ? settings.getDefaultRaceVessel()
        : settings.getRaceVesselByMMSI(this.getMMSI());
      if (raceVessel) {
        const settingsName = raceVessel.getName();
        // Sync the track's ship name with settings if different
        if (this.ship.shipName !== settingsName) {
          this.ship.shipName = settingsName;
        }
        return settingsName;
      }
    }
    return this.ship.shipName == null ? "Unnamed" : this.ship.shipName;
  }

  setShipName(name) {
    this.ship.shipName = name;
  }

  getFrames() {
    return this.ship?.data == null ? [] : this.ship?.data;
  }

  getPlayFrame() {
    return this.getFrames()[this.ui_currentFrameIndex];
  }

  getFrameIndexAtTs(ts) {
    return this.getFrames().findIndex(f => f.t.unix() > ts);
  }

  getRaceRankingFrame() {
    return this.ship?.data[Math.min(this.getRaceFinishedIdx(), this.ui_currentFrameIndex)];
  }

  getMaxProgress() {
    const frames = this.getFrames();
    if (frames.length === 0) return 0;
    return frames.reduce((max, frame) => {
      const progress = Sail.frameGetCalcField(frame, 'progressM');
      return progress != null ? Math.max(max, progress) : max;
    }, 0);
  }

  getRaceFinishedFrame() {
    return this.ship?.data[this.getRaceFinishedIdx()];
  }

  getRaceFinishedIdx() {
    if (this.finishedIdx == null) {
      this.finishedIdx = this.ship?.data.findIndex(x => Sail.frameGetCalcField(x, 'legId') == 'F');
      if (this.finishedIdx < 0)
        this.finishedIdx = this.ship?.data.length - 1;
    }
    return this.finishedIdx;
  }

  didFinishRace() {
    return this.getFrames().length - 1 > this.getRaceFinishedIdx();
  }
  
  getPlayIndex() {
    return this.ui_currentFrameIndex == null ? 0 : this.ui_currentFrameIndex;
  }

  setPlayIndex(idx) {
    this.ui_currentFrameIndex = idx;
  }

  setPlayTs(ts) {
    this.ui_playTs = ts;
  }

  getPlayTsSec() {
    return this.ui_playTs;
  }

  isVisible() {
    return this.ship.show;
  }
  setVisible(flag) {
    return this.ship.show = flag;
  }
  getColor(settings = null) {
    if (settings) {
      const raceVessel = this.isTypeVessel()
        ? settings.getDefaultRaceVessel()
        : settings.getRaceVesselByMMSI(this.getMMSI());
      if (raceVessel) {
        return raceVessel.getColor();
      }
    }

    // Return stored color if available
    if (this.color) {
      return this.color;
    }

    // Fallback to default colors
    if (this.isTypeVessel()) {
      return BOAT_COLORS_ARR[0];
    } else {
      return BOAT_COLORS_ARR[this.id % BOAT_COLORS_ARR.length];
    }
  }

  setColor(color) {
    this.color = color;
  }

  getHandicap() {
    return this.handicap;
  }

  setHandicap(handicap) {
    this.handicap = handicap;
  }

}